import { PIN_MAX, pin as pinSchema } from "@bt/domain";
import { useTranslation } from "react-i18next";
import { Field } from "@/components/app/field";
import { Input } from "@/components/ui/input";

/**
 * A PIN typed twice, on the desktop app with users (ADR 0013): in the welcome steps and the
 * profile. Only digits go in.
 */
export function PinFields({
  idPrefix,
  pin,
  repeat,
  onPin,
  onRepeat,
  errors,
  label,
  optional = false,
}: {
  idPrefix: string;
  pin: string;
  repeat: string;
  onPin: (value: string) => void;
  onRepeat: (value: string) => void;
  errors: { pin?: string; repeat?: string };
  label?: string;
  optional?: boolean;
}) {
  const { t } = useTranslation();
  const digits = (value: string) => value.replace(/\D/g, "").slice(0, PIN_MAX);
  return (
    <>
      <Field
        label={label ?? t("users.pin")}
        htmlFor={`${idPrefix}-pin`}
        error={errors.pin}
        hint={t("users.pinHint")}
        optional={optional}
      >
        <Input
          id={`${idPrefix}-pin`}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          aria-invalid={errors.pin ? true : undefined}
          onChange={(e) => onPin(digits(e.target.value))}
        />
      </Field>
      <Field label={t("users.repeatPin")} htmlFor={`${idPrefix}-repeat`} error={errors.repeat}>
        <Input
          id={`${idPrefix}-repeat`}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={repeat}
          aria-invalid={errors.repeat ? true : undefined}
          onChange={(e) => onRepeat(digits(e.target.value))}
        />
      </Field>
    </>
  );
}

/** Errors of a new PIN and its repetition, or null when both are fine. */
export function pinErrors(pin: string, repeat: string): { pin?: string; repeat?: string } | null {
  const parsed = pinSchema.safeParse(pin);
  if (!parsed.success) return { pin: parsed.error.issues[0]?.message };
  if (pin !== repeat) return { repeat: "validation.pinsDiffer" };
  return null;
}
